import { useInView } from '@/hooks/useInView';
import { Link } from 'react-router-dom';
import {
  Monitor, Database, Shield, Cloud, HeartPulse,
  ChevronRight, CheckCircle, Wrench, Settings, Headphones, Server,
} from 'lucide-react';
import { useDynamicContent, usePublicSeo } from '@/hooks/useDynamicContent';
import { resolveIcon } from '@/utils/iconMap';
import Seo from '@/seo/Seo';

const CYBER_IMG = 'https://images.unsplash.com/photo-1608742213509-815b97c30b36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwzfHxjeWJlcnNlY3VyaXR5JTIwY29kZSUyMGRhdGElMjBkYXJrfGVufDB8fHx8MTc3NTk1MzQ5Mnww&ixlib=rb-4.1.0&q=85';
const ARCH_IMG = 'https://images.unsplash.com/photo-1770385437556-2006aa8b4691?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBhcmNoaXRlY3R1cmUlMjBidWlsZGluZyUyMGFic3RyYWN0fGVufDB8fHx8MTc3NTk1MzQ3OXww&ixlib=rb-4.1.0&q=85';

const categoryAccent = {
  'Technology Support Services': 'from-[#F2B705]/10 to-transparent',
  'IT Sustainment & Operations Support': 'from-blue-500/5 to-transparent',
  'Cybersecurity Support Services': 'from-red-500/5 to-transparent',
  'Program & Mission Support': 'from-green-500/5 to-transparent',
  'Enterprise Support Services': 'from-purple-500/5 to-transparent',
};

const DEFAULTS = {
  hero_title: 'Support Services',
  hero_title_gold: 'Built for Operations',
  hero_description: "Our services are structured around the real needs of operational environments. Technology support, systems sustainment, cybersecurity, and program coordination — delivered by professionals who understand how these environments work.",
  service_categories: [
    {
      category: 'Technology Support Services',
      icon: 'Monitor',
      items: [
        { icon: 'Monitor', title: 'Enterprise Technology Support', desc: 'We provide hands-on support for enterprise systems, applications, and operational technology environments. Our teams work within your existing infrastructure to keep systems functioning, users supported, and workflows moving.', benefits: ['Enterprise systems support and coordination', 'Application environment support', 'Workflow and process support', 'Technology modernization assistance'], useCases: ['Ongoing enterprise application support', 'User support operations and service desk', 'Systems coordination across multiple teams', 'Support during technology transitions'], outcomes: 'Our clients maintain consistent system availability and reduce operational disruptions through structured, responsive technology support.' },
        { icon: 'Headphones', title: 'User Support Operations', desc: 'Reliable end-user support that keeps people productive.', benefits: ['Structured service desk operations', 'Issue tracking and escalation management', 'Consistent response time standards', 'User training and guidance support'], useCases: ['Enterprise help desk operations', 'Tiered support for complex environments', 'Onboarding support for new systems', 'Recurring issue identification and resolution'], outcomes: 'Organized support operations that reduce recurring issues and keep end users working effectively.' },
        { icon: 'Cloud', title: 'Cloud & Infrastructure Support', desc: 'Support for cloud environments, infrastructure management, and migration activities.', benefits: ['Cloud environment management', 'Infrastructure monitoring support', 'Migration planning and execution support', 'Configuration management'], useCases: ['Ongoing cloud environment support', 'Infrastructure monitoring and maintenance', 'Cloud migration support activities', 'Hybrid environment coordination'], outcomes: 'Stable, well-managed infrastructure environments with clear operational processes and documentation.' },
      ],
    },
    {
      category: 'IT Sustainment & Operations Support',
      icon: 'Wrench',
      items: [
        { icon: 'Wrench', title: 'Systems Sustainment', desc: 'Keeping operational systems reliable over time is the core of what we do.', benefits: ['Lifecycle support and management', 'Operational continuity planning', 'Environment maintenance and monitoring', 'Process coordination and documentation'], useCases: ['Long-term systems sustainment programs', 'Operational environment maintenance', 'Issue tracking and resolution workflows', 'Transition support between system versions'], outcomes: 'Sustained system reliability with documented processes, clear accountability, and reduced unplanned downtime.' },
        { icon: 'HeartPulse', title: 'Healthcare IT Support', desc: 'Specialized support for healthcare technology environments including EHR systems, clinical applications, and health IT operations.', benefits: ['EHR environment support', 'Clinical application coordination', 'Health IT compliance support', 'MHS GENESIS operational support'], useCases: ['Healthcare systems sustainment', 'Clinical workflow support', 'EHR migration and transition support', 'Health data coordination'], outcomes: 'Reliable healthcare IT environments that support clinical operations and meet compliance requirements.' },
      ],
    },
    {
      category: 'Cybersecurity Support Services',
      icon: 'Shield',
      items: [
        { icon: 'Shield', title: 'Cybersecurity Support', desc: 'Practical cybersecurity support that helps organizations maintain secure operational environments.', benefits: ['Security coordination support', 'Compliance documentation and tracking', 'Access management support', 'Operational risk assessment support'], useCases: ['Security compliance support (NIST, RMF)', 'Identity and access management coordination', 'Security process documentation', 'Audit preparation and support'], outcomes: 'Organizations maintain compliant, secure environments with clear documentation and structured security processes.' },
      ],
    },
    {
      category: 'Program & Mission Support',
      icon: 'Settings',
      items: [
        { icon: 'Settings', title: 'Program Support Services', desc: 'Structured support for program operations including coordination, reporting, scheduling, documentation, and stakeholder communication.', benefits: ['Program coordination and scheduling', 'Reporting and documentation support', 'Stakeholder communication support', 'Process management and tracking'], useCases: ['Federal program office support', 'Reporting and compliance coordination', 'Multi-stakeholder program coordination', 'Operational readiness support'], outcomes: 'Well-coordinated programs with clear reporting, organized documentation, and consistent stakeholder communication.' },
      ],
    },
    {
      category: 'Enterprise Support Services',
      icon: 'Server',
      items: [
        { icon: 'Server', title: 'Enterprise Coordination & Business Process Support', desc: 'Support services that help organizations manage enterprise-level coordination, business processes, and operational workflows.', benefits: ['Business process support', 'Enterprise coordination services', 'Data management and reporting', 'Cross-functional workflow support'], useCases: ['Enterprise process improvement support', 'Organizational coordination services', 'Data and reporting coordination', 'Operational workflow management'], outcomes: 'Improved organizational coordination with documented processes and consistent operational support.' },
        { icon: 'Database', title: 'Data & Reporting Support', desc: 'Practical support for data management, reporting workflows, and information coordination.', benefits: ['Data quality management', 'Reporting workflow support', 'Information coordination', 'Analytics and dashboard support'], useCases: ['Recurring reporting support', 'Data migration and cleanup', 'Dashboard and visualization support', 'Data governance coordination'], outcomes: 'Reliable data and reporting processes that support informed decision-making across the organization.' },
      ],
    },
  ],
  cta_heading: 'Have a Specific Need?',
  cta_description: 'Every operational environment is different. Let us discuss your specific support requirements and how we can help.',
};

void [Monitor, Database, Shield, Cloud, HeartPulse, Wrench, Settings, Headphones, Server];

function Section({ children, className = '' }) {
  const [ref, inView] = useInView();
  return (
    <section ref={ref} className={`${className} transition-all duration-700 ${inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
      {children}
    </section>
  );
}

export default function ServicesPage() {
  const c = useDynamicContent('services', DEFAULTS);
  const seoOverrides = usePublicSeo('services');
  const categories = c.service_categories || [];

  return (
    <div className="min-h-screen bg-[#121212]">
      <Seo
        page="services"
        title={seoOverrides?.title}
        description={seoOverrides?.description}
        keywords={seoOverrides?.keywords}
        ogTitle={seoOverrides?.og_title}
        ogDescription={seoOverrides?.og_description}
      />
      <section data-testid="services-hero" className="relative pt-32 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={CYBER_IMG} alt="" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#121212]/60 via-[#121212]/80 to-[#121212]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <span className="inline-block text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold mb-4 border border-[#F2B705]/30 px-4 py-1.5">Our Capabilities</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tighter text-white mb-6">
            {c.hero_title}<br />
            <span className="gold-gradient-text">{c.hero_title_gold}</span>
          </h1>
          <p className="text-base md:text-lg text-[#A3A3A3] max-w-2xl leading-relaxed mb-10">{c.hero_description}</p>
          <div className="flex flex-wrap gap-3">
            {categories.map((cat) => (
              <span key={cat.category} className="border border-white/10 text-xs uppercase tracking-widest text-[#A3A3A3] px-4 py-2 hover:border-[#F2B705]/30 hover:text-[#F2B705] transition-all cursor-default">
                {cat.category}
              </span>
            ))}
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#F2B705]/40 to-transparent" />
      </section>

      {/* Image strip */}
      <section className="relative h-64 md:h-80 overflow-hidden">
        <div className="absolute inset-0 grid grid-cols-3 gap-0">
          <img src="https://images.unsplash.com/photo-1680992046626-418f7e910589?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxzZXJ2ZXIlMjByb29tJTIwZGF0YSUyMGNlbnRlciUyMGNsb3VkfGVufDB8fHx8MTc3NTk1NzIwN3ww&ixlib=rb-4.1.0&q=85" alt="Data center" className="w-full h-full object-cover" />
          <img src="https://images.unsplash.com/photo-1708807472445-d33589e6b090?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxtaWxpdGFyeSUyMHRlY2hub2xvZ3klMjBkZWZlbnNlJTIwY3liZXJzZWN1cml0eSUyMG9wZXJhdGlvbnN8ZW58MHx8fHwxNzc1OTU3MTk1fDA&ixlib=rb-4.1.0&q=85" alt="Operations center" className="w-full h-full object-cover" />
          <img src="https://images.unsplash.com/photo-1758518729685-f88df7890776?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjBidXNpbmVzcyUyMHRlY2hub2xvZ3klMjBjb25zdWx0aW5nJTIwdGVhbSUyMHByb2Zlc3Npb25hbHxlbnwwfHx8fDE3NzU5NTcxODl8MA&ixlib=rb-4.1.0&q=85" alt="Team meeting" className="w-full h-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-[#121212] via-transparent to-[#121212]" />
        <div className="absolute inset-0 bg-[#121212]/40" />
      </section>

      {categories.map((cat, catIdx) => {
        const CatIcon = resolveIcon(cat.icon, Monitor);
        const accent = categoryAccent[cat.category] || 'from-[#F2B705]/10 to-transparent';
        const isAlt = catIdx % 2 !== 0;
        return (
          <Section key={cat.category || catIdx} className={`py-20 md:py-24 ${isAlt ? 'bg-[#161616]' : ''} relative overflow-hidden`}>
            <div className={`absolute top-0 left-0 w-full h-40 bg-gradient-to-b ${accent} pointer-events-none`} />
            <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
              <div className="flex items-center gap-4 mb-12">
                <div className="w-10 h-10 border border-[#F2B705]/30 flex items-center justify-center">
                  <CatIcon className="text-[#F2B705]" size={20} strokeWidth={1.5} />
                </div>
                <div>
                  <span className="text-xs tracking-[0.3em] uppercase text-[#F2B705] font-bold">{cat.category}</span>
                  <div className="w-16 h-px bg-[#F2B705]/40 mt-2" />
                </div>
              </div>

              {(cat.items || []).map((svc, i) => {
                const SvcIcon = resolveIcon(svc.icon, Monitor);
                const globalIdx = `${catIdx}-${i}`;
                return (
                  <div key={svc.title || globalIdx} className={`grid grid-cols-1 lg:grid-cols-2 gap-16 ${i > 0 ? 'mt-16 pt-16 border-t border-white/5' : ''}`}>
                    <div>
                      <div className="flex items-center gap-4 mb-6">
                        <div className="w-12 h-12 border border-[#F2B705]/30 flex items-center justify-center">
                          <SvcIcon className="text-[#F2B705]" size={24} strokeWidth={1.5} />
                        </div>
                        <h2 data-testid={`service-title-${globalIdx}`} className="text-2xl font-bold text-white">{svc.title}</h2>
                      </div>
                      <p className="text-sm text-[#A3A3A3] leading-relaxed mb-8">{svc.desc}</p>
                      <div className="bg-[#161616] border border-white/10 p-6 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-1 h-full bg-[#F2B705]" />
                        <p className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-3 pl-4">Outcomes</p>
                        <p className="text-sm text-white leading-relaxed pl-4">{svc.outcomes}</p>
                      </div>
                    </div>
                    <div className="space-y-8">
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-4">Key Benefits</h4>
                        <ul className="space-y-3">
                          {(svc.benefits || []).map((b) => (
                            <li key={b} className="flex items-start gap-3">
                              <CheckCircle size={16} className="text-[#F2B705] mt-0.5 shrink-0" />
                              <span className="text-sm text-[#A3A3A3]">{b}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <h4 className="text-xs uppercase tracking-widest text-[#F2B705] font-bold mb-4">Use Cases</h4>
                        <ul className="space-y-3">
                          {(svc.useCases || svc.use_cases || []).map((u) => (
                            <li key={u} className="flex items-start gap-3">
                              <ChevronRight size={14} className="text-[#525252] mt-0.5 shrink-0" />
                              <span className="text-sm text-[#A3A3A3]">{u}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/5 to-transparent" />
          </Section>
        );
      })}

      {/* CTA */}
      <Section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={ARCH_IMG} alt="" className="w-full h-full object-cover opacity-10" />
          <div className="absolute inset-0 bg-[#121212]/90" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-white mb-6">{c.cta_heading}</h2>
          <p className="text-base text-[#A3A3A3] max-w-xl mx-auto mb-10">{c.cta_description}</p>
          <Link to="/contact" data-testid="services-cta-btn" className="bg-[#F2B705] text-black font-bold text-sm uppercase tracking-wide px-10 py-4 hover:bg-[#D9A404] transition-colors inline-flex items-center gap-2">
            Request Consultation <ChevronRight size={16} />
          </Link>
        </div>
      </Section>
    </div>
  );
}
